import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { patientApi, notificationApi, moduleApi, navPrefsApi } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import CustomizeMenuDialog from '../components/layout/CustomizeMenuDialog';
import TrialBanner from '../components/TrialBanner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import VoiceNoteDialog from '../components/voice/VoiceNoteDialog';
import SubmitFormRequestDialog from '../components/SubmitFormRequestDialog';
import { useSubscription, featureKeyForPath } from '../contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import {
  Search, Bell, User, LogOut, Settings, HelpCircle, Menu, X,
  LayoutDashboard, Users, Calendar, MessageSquare, FileText, 
  Activity, DollarSign, Building2, ShieldCheck, BarChart3,
  Stethoscope, ClipboardList, Heart, Pill, Command, Video,
  Ticket, Key, AlertTriangle, Paintbrush, Database, TrendingUp,
  LayoutGrid, Shield, Lightbulb, BookOpen, Link2, Webhook,
  Target, Zap, Mail, Cloud, FormInput, Blocks, Home, Navigation,
  Star, Sliders, GraduationCap, MessagesSquare, ShieldAlert, UserPlus, KeyRound, LifeBuoy, Package, IdCard, Mic, Inbox, Tag, Plus, FileSpreadsheet, CalendarClock,
} from 'lucide-react';

// Navigation items by role
const navigationByRole = {
  super_admin: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'My Dashboards', icon: BarChart3, path: '/my-dashboards' },
    { name: 'Tasks', icon: ClipboardList, path: '/tasks' },
    { name: 'Organizations', icon: Building2, path: '/admin/organizations' },
    { name: 'Access Requests', icon: UserPlus, path: '/admin/access-requests' },
    { name: 'Support (CSM)', icon: LifeBuoy, path: '/admin/csm' },
    { name: 'Assets', icon: Package, path: '/assets' },
    { name: 'HR & ID Cards', icon: IdCard, path: '/hr' },
    { name: 'User Mgmt', icon: Users, path: '/admin/users' },
    { name: 'Patient Tabs', icon: LayoutGrid, path: '/admin/patient-tabs' },
    { name: 'Pricing', icon: DollarSign, path: '/admin/pricing' },
    { name: 'Subscriptions', icon: DollarSign, path: '/admin/subscriptions' },
    { name: 'Patients', icon: Heart, path: '/patients' },
    { name: 'CRM', icon: Target, path: '/crm' },
    { name: 'Leads', icon: Users, path: '/crm/leads' },
    { name: 'Campaigns', icon: Zap, path: '/crm/campaigns' },
    { name: 'Templates', icon: Mail, path: '/crm/templates' },
    { name: 'Exec. Insights', icon: Lightbulb, path: '/insights' },
    { name: 'CQM Dashboard', icon: ShieldCheck, path: '/cqm' },
    { name: 'Home Care Dashboard', icon: Home, path: '/home-care' },
    { name: 'EVV Dashboard', icon: Navigation, path: '/admin/evv' },
    { name: 'Pop. Health', icon: TrendingUp, path: '/analytics/population-health' },
    { name: 'Reports', icon: BarChart3, path: '/reports' },
    { name: 'Widget Builder', icon: LayoutGrid, path: '/widgets' },
    { name: 'Claims', icon: DollarSign, path: '/claims' },
    { name: 'Claims Tracker', icon: Activity, path: '/claims/tracker' },
    { name: 'Denial Prevention', icon: ShieldCheck, path: '/denials' },
    { name: 'Claims Integration', icon: Link2, path: '/claims/integration' },
    { name: 'MyChart', icon: Cloud, path: '/mychart' },
    { name: 'EHR Integration', icon: Database, path: '/ehr' },
    { name: 'EHR Settings', icon: Settings, path: '/ehr/settings' },
    { name: 'FHIR Webhooks', icon: Activity, path: '/fhir/webhooks' },
    { name: 'Audit Logs', icon: Shield, path: '/admin/audit-logs' },
    { name: 'Inbox', icon: Inbox, path: '/admin/inbox' },
    { name: 'Branding', icon: Paintbrush, path: '/admin/branding' },
    { name: 'Modules', icon: Blocks, path: '/admin/modules' },
    { name: 'Courses (Admin)', icon: GraduationCap, path: '/admin/lms' },
    { name: 'Articles (Admin)', icon: BookOpen, path: '/admin/knowledge' },
    { name: 'Forum (Admin)', icon: MessagesSquare, path: '/admin/forum' },
    { name: 'Learn', icon: GraduationCap, path: '/learn' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Forms', icon: FormInput, path: '/forms' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  practice_admin: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'My Dashboards', icon: BarChart3, path: '/my-dashboards' },
    { name: 'Tasks', icon: ClipboardList, path: '/tasks' },
    { name: 'User Mgmt', icon: Users, path: '/admin/users' },
    { name: 'Access Codes', icon: KeyRound, path: '/admin/access-codes' },
    { name: 'Access Requests', icon: UserPlus, path: '/admin/access-requests' },
    { name: 'Assets', icon: Package, path: '/assets' },
    { name: 'HR & ID Cards', icon: IdCard, path: '/hr' },
    { name: 'Patient Tabs', icon: LayoutGrid, path: '/admin/patient-tabs' },
    { name: 'Bulk Uploads', icon: FileSpreadsheet, path: '/admin/bulk-uploads' },
    { name: 'Service Requests', icon: ClipboardList, path: '/admin/service-requests' },
    { name: 'Role Labels', icon: Tag, path: '/admin/role-labels' },
    { name: 'Subscription', icon: DollarSign, path: '/subscription' },
    { name: 'Patients', icon: Heart, path: '/patients' },
    { name: 'Scheduling', icon: Calendar, path: '/appointments' },
    { name: 'Exec. Insights', icon: Lightbulb, path: '/insights' },
    { name: 'CQM Dashboard', icon: ShieldCheck, path: '/cqm' },
    { name: 'Home Care Dashboard', icon: Home, path: '/home-care' },
    { name: 'EVV Dashboard', icon: Navigation, path: '/admin/evv' },
    { name: 'Pop. Health', icon: TrendingUp, path: '/analytics/population-health' },
    { name: 'Reports', icon: BarChart3, path: '/reports' },
    { name: 'Widget Builder', icon: LayoutGrid, path: '/widgets' },
    { name: 'Claims', icon: DollarSign, path: '/claims' },
    { name: 'Claims Tracker', icon: Activity, path: '/claims/tracker' },
    { name: 'Denial Prevention', icon: ShieldCheck, path: '/denials' },
    { name: 'MyChart', icon: Cloud, path: '/mychart' },
    { name: 'EHR Integration', icon: Database, path: '/ehr' },
    { name: 'Audit Logs', icon: Shield, path: '/admin/audit-logs' },
    { name: 'Inbox', icon: Inbox, path: '/admin/inbox' },
    { name: 'Branding', icon: Paintbrush, path: '/admin/branding' },
    { name: 'Courses (Admin)', icon: GraduationCap, path: '/admin/lms' },
    { name: 'Articles (Admin)', icon: BookOpen, path: '/admin/knowledge' },
    { name: 'Forum (Admin)', icon: MessagesSquare, path: '/admin/forum' },
    { name: 'Learn', icon: GraduationCap, path: '/learn' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Forms', icon: FormInput, path: '/forms' },
    { name: 'Due Forms', icon: CalendarClock, path: '/forms/due' },
    { name: 'Form Library', icon: BookOpen, path: '/forms/library' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  physician: [
    { name: 'My Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'My Dashboards', icon: BarChart3, path: '/my-dashboards' },
    { name: 'Tasks', icon: ClipboardList, path: '/tasks' },
    { name: 'My Patients', icon: Users, path: '/patients' },
    { name: "Today's Schedule", icon: Calendar, path: '/appointments' },
    { name: 'Clinical Notes', icon: FileText, path: '/notes' },
    { name: 'E-Prescribe', icon: Pill, path: '/prescriptions' },
    { name: 'MyChart', icon: Cloud, path: '/mychart' },
    { name: 'CQM Dashboard', icon: ShieldCheck, path: '/cqm' },
    { name: 'Home Care Dashboard', icon: Home, path: '/home-care' },
    { name: 'Pop. Health', icon: TrendingUp, path: '/analytics/population-health' },
    { name: 'Reports', icon: BarChart3, path: '/reports' },
    { name: 'Widget Builder', icon: LayoutGrid, path: '/widgets' },
    { name: 'Telehealth', icon: Video, path: '/appointments' },
    { name: 'Incidents', icon: ShieldAlert, path: '/incidents' },
    { name: 'Learn', icon: GraduationCap, path: '/learn' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Forms', icon: FormInput, path: '/forms' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  nurse: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'My Dashboards', icon: BarChart3, path: '/my-dashboards' },
    { name: 'Tasks', icon: ClipboardList, path: '/tasks' },
    { name: 'My Patients', icon: Users, path: '/patients' },
    { name: 'Appointments', icon: Calendar, path: '/appointments' },
    { name: 'EVV Dashboard', icon: Navigation, path: '/admin/evv' },
    { name: 'Prescriptions', icon: Pill, path: '/prescriptions' },
    { name: 'Reports', icon: BarChart3, path: '/reports' },
    { name: 'Incidents', icon: ShieldAlert, path: '/incidents' },
    { name: 'Learn', icon: GraduationCap, path: '/learn' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Forms', icon: FormInput, path: '/forms' },
    { name: 'Due Forms', icon: CalendarClock, path: '/forms/due' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  care_coordinator: [
    { name: 'My Workload', icon: ClipboardList, path: '/dashboard' },
    { name: 'Patients', icon: Users, path: '/patients' },
    { name: 'Incidents', icon: ShieldAlert, path: '/incidents' },
    { name: 'Reports', icon: BarChart3, path: '/reports' },
    { name: 'Tasks', icon: ClipboardList, path: '/tasks' },
    { name: 'Learn', icon: GraduationCap, path: '/learn' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Forms', icon: FormInput, path: '/forms' },
    { name: 'Due Forms', icon: CalendarClock, path: '/forms/due' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  billing_specialist: [
    { name: 'Claims Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'My Dashboards', icon: BarChart3, path: '/my-dashboards' },
    { name: 'Tasks', icon: ClipboardList, path: '/tasks' },
    { name: 'Claim Queue', icon: FileText, path: '/claims' },
    { name: 'Claims Tracker', icon: Activity, path: '/claims/tracker' },
    { name: 'Denial Prevention', icon: ShieldCheck, path: '/denials' },
    { name: 'Claims Integration', icon: Link2, path: '/claims/integration' },
    { name: 'Reports', icon: BarChart3, path: '/reports' },
    { name: 'Widget Builder', icon: LayoutGrid, path: '/widgets' },
    { name: 'Patients', icon: Users, path: '/patients' },
    { name: 'Learn', icon: GraduationCap, path: '/learn' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Forms', icon: FormInput, path: '/forms' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  patient: [
    { name: 'My Health', icon: Heart, path: '/dashboard' },
    { name: 'Appointments', icon: Calendar, path: '/appointments' },
    { name: 'Medications', icon: Pill, path: '/prescriptions' },
    { name: 'Learn', icon: GraduationCap, path: '/learn' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  aide: [
    { name: 'My Visits', icon: Home, path: '/dashboard' },
    { name: 'Tasks', icon: ClipboardList, path: '/tasks' },
    { name: 'Report Incident', icon: ShieldAlert, path: '/incidents/new' },
    { name: 'My Incidents', icon: AlertTriangle, path: '/incidents' },
    { name: 'Learn', icon: GraduationCap, path: '/learn' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  director_patient_services: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'My Dashboards', icon: BarChart3, path: '/my-dashboards' },
    { name: 'Patients', icon: Heart, path: '/patients' },
    { name: 'User Mgmt', icon: Users, path: '/admin/users' },
    { name: 'Scheduling', icon: Calendar, path: '/appointments' },
    { name: 'Forms', icon: FormInput, path: '/forms' },
    { name: 'Due Forms', icon: CalendarClock, path: '/forms/due' },
    { name: 'Form Library', icon: BookOpen, path: '/forms/library' },
    { name: 'Bulk Uploads', icon: FileSpreadsheet, path: '/admin/bulk-uploads' },
    { name: 'Service Requests', icon: ClipboardList, path: '/admin/service-requests' },
    { name: 'Incidents', icon: ShieldAlert, path: '/incidents' },
    { name: 'CQM Dashboard', icon: ShieldCheck, path: '/cqm' },
    { name: 'Home Care Dashboard', icon: Home, path: '/home-care' },
    { name: 'EVV Dashboard', icon: Navigation, path: '/admin/evv' },
    { name: 'In-service', icon: GraduationCap, path: '/inservice' },
    { name: 'Reports', icon: BarChart3, path: '/reports' },
    { name: 'Audit Logs', icon: Shield, path: '/admin/audit-logs' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ],
  service_provider: [
    { name: 'My Portal', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'My Service Requests', icon: ClipboardList, path: '/admin/service-requests' },
    { name: 'Knowledge', icon: BookOpen, path: '/knowledge' },
    { name: 'In-service', icon: GraduationCap, path: '/inservice' },
    { name: 'Community', icon: MessagesSquare, path: '/forum' },
    { name: 'Messages', icon: MessageSquare, path: '/messages' },
    { name: 'Support', icon: Ticket, path: '/support' },
  ]
};

export default function DashboardLayout({ children }) {
  const { hasFeature } = useSubscription();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  // Translate a nav-item English label using the `nav.*` namespace. Falls
  // back to the original English string when no translation is registered.
  const tnav = (englishName) => {
    const map = {
      'Dashboard': 'dashboard',
      'My Dashboard': 'myDashboard',
      'My Dashboards': 'myDashboards',
      'Tasks': 'tasks',
      'Organizations': 'organizations',
      'Access Requests': 'accessRequests',
      'Support (CSM)': 'supportCsm',
      'Assets': 'assets',
      'HR & ID Cards': 'hr',
      'User Mgmt': 'userMgmt',
      'Patient Tabs': 'patientTabs',
      'Pricing': 'pricing',
      'Subscriptions': 'subscriptions',
      'Subscription': 'subscriptions',
      'Patients': 'patients',
      'CRM': 'crm',
      'Leads': 'leads',
      'Campaigns': 'campaigns',
      'Templates': 'templates',
      'Exec. Insights': 'execInsights',
      'CQM Dashboard': 'cqmDashboard',
      'Home Care Dashboard': 'homeCareDashboard',
      'EVV Dashboard': 'evvDashboard',
      'Pop. Health': 'popHealth',
      'Reports': 'reports',
      'Widget Builder': 'widgetBuilder',
      'Claims': 'claims',
      'My Patients': 'myPatients',
      'My Visits': 'myVisits',
      'Clinical Notes': 'notes',
      'Forms': 'forms',
      'Messages': 'messages',
      'Support': 'support',
      'Branding': 'branding',
      'Audit Logs': 'auditLog',
      'E-Prescribe': 'prescriptions',
      'Telehealth': 'telehealth',
      'Appointments': 'appointments',
      'Scheduling': 'scheduling',
      'Profile': 'profile',
      'Settings': 'settings',
    };
    const key = map[englishName];
    if (!key) return englishName;
    const translated = t(`nav.${key}`);
    // i18next returns the key path when missing — fall back to English.
    return translated === `nav.${key}` ? englishName : translated;
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  // Voice note recorder — clinical roles only
  const [showVoiceNote, setShowVoiceNote] = useState(false);
  const CLINICAL_ROLES = ['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide'];
  const canVoiceNote = CLINICAL_ROLES.includes(user?.role);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSubmitRequest, setShowSubmitRequest] = useState(false);
  const [enabledModules, setEnabledModules] = useState(null);
  const [navPrefs, setNavPrefs] = useState({ order: [], favorites: [], hidden: [] });
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Module-to-path mapping for filtering
  const MODULE_PATH_MAP = {
    patients: ['/patients'],
    appointments: ['/appointments'],
    messages: ['/messages'],
    forms: ['/forms'],
    ehr: ['/ehr', '/fhir/webhooks', '/mychart'],
    claims: ['/claims', '/denials'],
    crm: ['/crm'],
    cqm: ['/cqm'],
    analytics: ['/analytics', '/reports', '/insights', '/case-study'],
    mychart: ['/mychart'],
    prescriptions: ['/prescriptions'],
    telehealth: ['/telehealth'],
    support: ['/support'],
  };

  const rawNavigation = navigationByRole[user?.role] || navigationByRole.practice_admin;
  const moduleFiltered = enabledModules
    ? rawNavigation.filter(item => {
        // Always show dashboard and admin items
        if (item.path === '/dashboard' || item.path.startsWith('/admin')) return true;
        // Check if any module owns this path
        for (const [moduleKey, paths] of Object.entries(MODULE_PATH_MAP)) {
          if (paths.some(p => item.path.startsWith(p))) {
            return enabledModules.includes(moduleKey);
          }
        }
        return true; // Show items not mapped to any module
      })
    : rawNavigation;

  // Apply the user's nav prefs: hidden items are stripped, favourites are
  // pinned to the top (in the favourites' own order), and the remaining items
  // follow the user's saved order (new items the user hasn't seen yet are
  // appended at the bottom).
  const itemByPath = Object.fromEntries(moduleFiltered.map(i => [i.path, i]));
  const hiddenSet = new Set(navPrefs.hidden || []);
  const favSet = new Set((navPrefs.favorites || []).filter(p => !hiddenSet.has(p)));
  const favItems = (navPrefs.favorites || []).filter(p => !hiddenSet.has(p) && itemByPath[p]).map(p => itemByPath[p]);
  const savedOrder = (navPrefs.order || []).filter(p => !hiddenSet.has(p) && itemByPath[p] && !favSet.has(p));
  const orderedNonFavPaths = [
    ...savedOrder,
    ...moduleFiltered.map(i => i.path).filter(p => !hiddenSet.has(p) && !favSet.has(p) && !savedOrder.includes(p)),
  ];
  const orderedNonFav = orderedNonFavPaths.map(p => itemByPath[p]).filter(Boolean);
  // Keep `navigation` as a flat list for search/highlighting; use the
  // fav/non-fav split only for the sidebar render.
  const navigation = [...favItems, ...orderedNonFav];

  // Load enabled modules
  useEffect(() => {
    const loadModules = async () => {
      try {
        const res = await moduleApi.list();
        const mods = (res.data.modules || []).filter(m => m.enabled).map(m => m.key);
        setEnabledModules(mods);
      } catch { /* default: show all */ }
    };
    loadModules();
  }, []);

  // Load user sidebar preferences (order / favorites / hidden)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await navPrefsApi.get();
        setNavPrefs({
          order: data.order || [],
          favorites: data.favorites || [],
          hidden: data.hidden || [],
        });
      } catch { /* keep defaults */ }
    })();
  }, [user]);

  // Load notifications
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const res = await notificationApi.list();
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
      } catch (err) {
        // Silently fail - notifications are non-critical
      }
    };
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  // Check if impersonating (token-based)
  useEffect(() => {
    const impersonating = localStorage.getItem('savenes_impersonating');
    setIsImpersonating(!!impersonating);
  }, []);

  // Handle keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search patients
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const debounce = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await patientApi.list({ search: searchQuery, limit: 10 });
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleStopImpersonation = async () => {
    try {
      const originalAdminId = localStorage.getItem('savenes_original_admin');
      const res = await api.post('/admin/stop-impersonation', { original_admin_id: originalAdminId });
      localStorage.setItem('savenes_token', res.data.access_token);
      localStorage.removeItem('savenes_impersonating');
      localStorage.removeItem('savenes_original_admin');
      toast.success('Returned to admin account');
      setIsImpersonating(false);
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('Failed to stop impersonation');
    }
  };

  const handlePatientSelect = (patientId) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`/patients/${patientId}`);
  };

  const getRoleDisplayName = (role) => {
    const names = {
      super_admin: 'Super Admin',
      practice_admin: 'Practice Admin',
      physician: 'Physician',
      nurse: 'Nurse',
      aide: 'Home Care Aide',
      billing_specialist: 'Billing Specialist',
      patient: 'Patient'
    };
    return names[role] || role;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#92400E] text-white py-2 px-4 flex items-center justify-center gap-4">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">{t('topbar.impersonating', { name: user?.name, role: user?.role })}</span>
          <Button 
            size="sm" 
            variant="secondary" 
            className="bg-white text-[#92400E] hover:bg-white/90 h-7 text-xs"
            onClick={handleStopImpersonation}
            data-testid="stop-impersonation"
          >
            {t('topbar.returnToAdmin')}
          </Button>
        </div>
      )}

      {/* Top Bar */}
      <header className={`glass-header fixed left-0 right-0 z-40 h-16 ${isImpersonating ? 'top-10' : 'top-0'}`}>
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          {/* Left: Logo & Mobile Menu */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-[#F1F5F9]"
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[#0E7490] flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <span className="hidden sm:block text-xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Savenes
              </span>
            </Link>
          </div>

          {/* Center: Search */}
          {user?.role !== 'patient' && (
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-[#F1F5F9] rounded-full text-[#64748B] hover:bg-[#E2E8F0] transition-colors max-w-md flex-1 mx-8"
              data-testid="global-search-button"
            >
              <Search className="w-4 h-4" />
              <span>{t('topbar.searchPatients')}</span>
              <kbd className="ml-auto px-2 py-0.5 bg-white rounded text-xs border border-[#E2E8F0]">
                <Command className="w-3 h-3 inline" />K
              </kbd>
            </button>
          )}

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="topbar" testId="topbar-language-switcher" />
            {/* Submit a form request — available to every signed-in user. */}
            <button
              className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#0E7490]/10 text-[#0E7490] hover:bg-[#0E7490]/20 text-xs font-medium"
              onClick={() => setShowSubmitRequest(true)}
              title="Submit a form request"
              data-testid="topbar-submit-request-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Submit request
            </button>
            {canVoiceNote && (
              <button
                className="p-2 rounded-lg hover:bg-[#F1F5F9] relative group"
                onClick={() => setShowVoiceNote(true)}
                title="Voice Note — record & summarize"
                data-testid="topbar-voice-note-btn"
              >
                <Mic className="w-5 h-5 text-[#0E7490]" />
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-[#0A2342] bg-white px-1.5 py-0.5 rounded shadow border border-[#E2E8F0] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Voice Note</span>
              </button>
            )}
            <button 
              className="p-2 rounded-lg hover:bg-[#F1F5F9] relative"
              data-testid="notifications-button"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell className="w-5 h-5 text-[#64748B]" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-[#991B1B] rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div className="absolute right-16 top-14 w-80 bg-white rounded-xl shadow-xl border border-[#E2E8F0] z-50 max-h-[400px] overflow-y-auto" data-testid="notifications-panel">
                <div className="p-3 border-b border-[#E2E8F0] flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#0A2342]">{t('topbar.notifications')}</span>
                  {notifications.length > 0 && <button onClick={async () => { try { await api.post('/notifications/mark-read'); setUnreadCount(0); setNotifications(prev => prev.map(n => ({...n, read: true}))); } catch {} }} className="text-[10px] text-[#0E7490] hover:underline">Mark all read</button>}
                </div>
                {notifications.length > 0 ? notifications.slice(0, 15).map((n, i) => (
                  <div key={n.id || i} className={`p-3 border-b border-[#F1F5F9] text-xs hover:bg-[#F8FAFC] cursor-pointer ${n.read ? '' : 'bg-[#0E7490]/5'}`} onClick={() => { if (n.link) navigate(n.link); setShowNotifications(false); }}>
                    <div className="font-medium text-[#0A2342]">{n.title}</div>
                    <div className="text-[#64748B] mt-0.5">{n.message}</div>
                    <div className="text-[#94A3B8] mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</div>
                  </div>
                )) : (
                  <div className="p-6 text-center text-xs text-[#94A3B8]">{t('topbar.noNotifications')}</div>
                )}
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#F1F5F9]"
                  data-testid="user-menu-button"
                >
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="avatar" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 bg-[#0E7490] rounded-full flex items-center justify-center text-white font-medium">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium text-[#0A2342]">{user?.name}</div>
                    <div className="text-xs text-[#64748B]">{getRoleDisplayName(user?.role)}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/settings/profile')} data-testid="menu-profile-link">
                  <User className="w-4 h-4" />
                  {t('topbar.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/settings/security')} data-testid="menu-security-link">
                  <ShieldCheck className="w-4 h-4" />
                  Security & MFA
                </DropdownMenuItem>
                {['super_admin', 'practice_admin'].includes(user?.role) && (
                  <DropdownMenuItem className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/admin/settings')} data-testid="menu-settings-link">
                    <Settings className="w-4 h-4" />
                    {t('topbar.settings')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/support')} data-testid="menu-support-link">
                  <HelpCircle className="w-4 h-4" />
                  {t('topbar.help')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="flex items-center gap-2 text-[#991B1B]"
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                  {t('topbar.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed left-0 bottom-0 w-64 bg-white border-r border-[#E2E8F0] z-30 transform transition-transform duration-200 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } ${isImpersonating ? 'top-[104px]' : 'top-16'}`}>
        <ScrollArea className="h-full py-4">
          <nav className="px-3 space-y-1 pb-20">
            {favItems.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 px-3 pt-1 pb-1 text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">
                  <Star className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" />Favorites
                </div>
                {favItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const featKey = featureKeyForPath(item.path);
                  const locked = !hasFeature(featKey);
                  if (locked) {
                    return (
                      <button
                        key={`fav-${item.path}`}
                        type="button"
                        onClick={() => navigate('/admin/settings?tab=billing')}
                        className="sidebar-item w-full opacity-60"
                        data-testid={`nav-fav-locked-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        title={`${tnav(item.name)} — upgrade to unlock`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="flex items-center gap-1">{tnav(item.name)}<Lock className="w-3 h-3 ml-1 text-[#94A3B8]" /></span>
                      </button>
                    );
                  }
                  return (
                    <Link
                      key={`fav-${item.path}`}
                      to={item.path}
                      className={`sidebar-item ${isActive ? 'active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-fav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{tnav(item.name)}</span>
                    </Link>
                  );
                })}
                <div className="border-t border-[#F1F5F9] my-2 mx-2" />
              </>
            )}
            {orderedNonFav.map((item) => {
              const isActive = location.pathname === item.path;
              const featKey = featureKeyForPath(item.path);
              const locked = !hasFeature(featKey);
              if (locked) {
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => navigate('/admin/settings?tab=billing')}
                    className="sidebar-item w-full opacity-60"
                    data-testid={`nav-locked-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    title={`${tnav(item.name)} — upgrade to unlock`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex items-center gap-1">{tnav(item.name)}<Lock className="w-3 h-3 ml-1 text-[#94A3B8]" /></span>
                  </button>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{tnav(item.name)}</span>
                </Link>
              );
            })}
            {/* Customize trigger */}
            <button
              type="button"
              onClick={() => setCustomizeOpen(true)}
              className="sidebar-item w-full !text-[#64748B] hover:!text-[#0E7490] border-t border-[#F1F5F9] mt-2 pt-3"
              data-testid="customize-menu-btn"
            >
              <Sliders className="w-5 h-5" />
              <span>Customize Menu</span>
            </button>
          </nav>

          {/* Bottom section */}
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[#E2E8F0]">
            <div className="px-2 text-xs text-[#94A3B8]">
              <kbd className="px-1 bg-[#F1F5F9] rounded">⌘K</kbd> Search •{' '}
              <kbd className="px-1 bg-[#F1F5F9] rounded">?</kbd> Shortcuts
            </div>
          </div>
        </ScrollArea>
      </aside>

      {/* Main Content */}
      <main className={`lg:ml-64 min-h-screen ${isImpersonating ? 'pt-[104px]' : 'pt-16'}`}>
        {/* Trial banner — sticky just below the top bar so it's always visible */}
        <TrialBanner />
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="sm:max-w-xl p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="sr-only">Search Patients</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
              <Input
                placeholder="Search patients by name or MRN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-6 text-lg"
                autoFocus
                data-testid="global-search-input"
              />
            </div>
          </div>
          
          <div className="border-t border-[#E2E8F0] max-h-80 overflow-y-auto">
            {searching ? (
              <div className="p-8 text-center text-[#64748B]">
                <div className="w-6 h-6 border-2 border-[#0E7490] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                Searching...
              </div>
            ) : searchResults.length > 0 ? (
              <div className="divide-y divide-[#E2E8F0]">
                {searchResults.map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => handlePatientSelect(patient.id)}
                    className="w-full p-4 hover:bg-[#F8FAFC] flex items-center gap-4 text-left"
                    data-testid={`search-result-${patient.id}`}
                  >
                    <div className="w-10 h-10 bg-[#0E7490]/10 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-[#0E7490]" />
                    </div>
                    <div>
                      <div className="font-medium text-[#0A2342]">
                        {patient.first_name} {patient.last_name}
                      </div>
                      <div className="text-sm text-[#64748B]">
                        MRN: {patient.mrn} • {patient.date_of_birth}
                      </div>
                    </div>
                    {patient.risk_score >= 70 && (
                      <span className="risk-badge-high ml-auto">High Risk</span>
                    )}
                  </button>
                ))}
              </div>
            ) : searchQuery ? (
              <div className="p-8 text-center text-[#64748B]">
                No patients found matching "{searchQuery}"
              </div>
            ) : (
              <div className="p-8 text-center text-[#64748B]">
                Start typing to search patients...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Customize sidebar menu */}
      <CustomizeMenuDialog
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        baseItems={moduleFiltered}
        initial={navPrefs}
        onSaved={(p) => setNavPrefs({
          order: p.order || [],
          favorites: p.favorites || [],
          hidden: p.hidden || [],
        })}
      />

      {/* Global voice-note dialog — opened from the top-bar mic button */}
      {canVoiceNote && (
        <VoiceNoteDialog
          open={showVoiceNote}
          onOpenChange={setShowVoiceNote}
        />
      )}

      {/* Global "Submit a form request" — available to every signed-in user */}
      <SubmitFormRequestDialog
        open={showSubmitRequest}
        onClose={() => setShowSubmitRequest(false)}
        onSubmitted={() => setShowSubmitRequest(false)}
      />
    </div>
  );
}
